import React, { createContext, useContext, useState } from 'react';

interface ExternalInputState {
  // Search and Filter State
  externalKeywordsList: string[];
  setExternalKeywordsList: React.Dispatch<React.SetStateAction<string[]>>;
  externalFilterId: string;
  setExternalFilterId: React.Dispatch<React.SetStateAction<string>>;
  externalLockedProducerLiterals: string[];
  setExternalLockedProducerLiterals: React.Dispatch<React.SetStateAction<string[]>>;
  externalSearchPartial: boolean;
  setExternalSearchPartial: React.Dispatch<React.SetStateAction<boolean>>;
  externalSearchPartialProducer: boolean;
  setExternalSearchPartialProducer: React.Dispatch<React.SetStateAction<boolean>>;

  // Table Data State
  externalFetchedWines: any[];
  setExternalFetchedWines: React.Dispatch<React.SetStateAction<any[]>>;
  externalFetchedProducers: any[];
  setExternalFetchedProducers: React.Dispatch<React.SetStateAction<any[]>>;
  externalSelectedWineRows: any[];
  setExternalSelectedWineRows: React.Dispatch<React.SetStateAction<any[]>>;
  externalMergeModalWines: any[];
  setExternalMergeModalWines: React.Dispatch<React.SetStateAction<any[]>>;

  // Pagination State
  externalWinesPage: number;
  setExternalWinesPage: React.Dispatch<React.SetStateAction<number>>;
  externalProducersPage: number;
  setExternalProducersPage: React.Dispatch<React.SetStateAction<number>>;
  externalHasMoreWines: boolean;
  setExternalHasMoreWines: React.Dispatch<React.SetStateAction<boolean>>;
  externalHasMoreProducers: boolean;
  setExternalHasMoreProducers: React.Dispatch<React.SetStateAction<boolean>>;
  externalTotalRowsOfWines: number;
  setExternalTotalRowsOfWines: React.Dispatch<React.SetStateAction<number>>;
  externalTotalRowsOfProducers: number;
  setExternalTotalRowsOfProducers: React.Dispatch<React.SetStateAction<number>>;

  // UI State
  externalShowMergeModal: boolean;
  setExternalShowMergeModal: React.Dispatch<React.SetStateAction<boolean>>;
  externalShowProducerModal: boolean;
  setExternalShowProducerModal: React.Dispatch<React.SetStateAction<boolean>>;
  externalSelectedProducerId: string | null;
  setExternalSelectedProducerId: React.Dispatch<React.SetStateAction<string | null>>;

  // External Input File Data State
  externalNumberOfFiles: number;
  setExternalNumberOfFiles: React.Dispatch<React.SetStateAction<number>>;
  externalUniqueKey: string;
  setExternalUniqueKey: React.Dispatch<React.SetStateAction<string>>;
  externalFileName: string;
  setExternalFileName: React.Dispatch<React.SetStateAction<string>>;
  externalTableData: any[];
  setExternalTableData: React.Dispatch<React.SetStateAction<any[]>>;
  externalCurrentIndex: number;
  setExternalCurrentIndex: React.Dispatch<React.SetStateAction<number>>;
  externalDataLoaded: boolean;
  setExternalDataLoaded: React.Dispatch<React.SetStateAction<boolean>>;
  externalMatchedWine: any | null;
  setExternalMatchedWine: React.Dispatch<React.SetStateAction<any | null>>;
}

export const ExternalInputContext = createContext<ExternalInputState | undefined>(undefined);

export const ExternalInputProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Search and Filter State
  const [externalKeywordsList, setExternalKeywordsList] = useState<string[]>([]);
  const [externalFilterId, setExternalFilterId] = useState('');
  const [externalLockedProducerLiterals, setExternalLockedProducerLiterals] = useState<string[]>([]);
  const [externalSearchPartial, setExternalSearchPartial] = useState(true);
  const [externalSearchPartialProducer, setExternalSearchPartialProducer] = useState(true);

  // Table Data State
  const [externalFetchedWines, setExternalFetchedWines] = useState<any[]>([]);
  const [externalFetchedProducers, setExternalFetchedProducers] = useState<any[]>([]);
  const [externalSelectedWineRows, setExternalSelectedWineRows] = useState<any[]>([]);
  const [externalMergeModalWines, setExternalMergeModalWines] = useState<any[]>([]);

  // Pagination State
  const [externalWinesPage, setExternalWinesPage] = useState(1);
  const [externalProducersPage, setExternalProducersPage] = useState(1);
  const [externalHasMoreWines, setExternalHasMoreWines] = useState(true);
  const [externalHasMoreProducers, setExternalHasMoreProducers] = useState(true);
  const [externalTotalRowsOfWines, setExternalTotalRowsOfWines] = useState(0);
  const [externalTotalRowsOfProducers, setExternalTotalRowsOfProducers] = useState(0);

  // UI State
  const [externalShowMergeModal, setExternalShowMergeModal] = useState(false);
  const [externalShowProducerModal, setExternalShowProducerModal] = useState(false);
  const [externalSelectedProducerId, setExternalSelectedProducerId] = useState<string | null>(null);

  // External Input File Data State
  const [externalNumberOfFiles, setExternalNumberOfFiles] = useState(0);
  const [externalUniqueKey, setExternalUniqueKey] = useState('');
  const [externalFileName, setExternalFileName] = useState('');
  const [externalTableData, setExternalTableData] = useState<any[]>([]);
  const [externalCurrentIndex, setExternalCurrentIndex] = useState(0);
  const [externalDataLoaded, setExternalDataLoaded] = useState(false);
  const [externalMatchedWine, setExternalMatchedWine] = useState<any | null>(null);

  return (
    <ExternalInputContext.Provider
      value={{
        // Search and Filter State
        externalKeywordsList,
        setExternalKeywordsList,
        externalFilterId,
        setExternalFilterId,
        externalLockedProducerLiterals,
        setExternalLockedProducerLiterals,
        externalSearchPartial,
        setExternalSearchPartial,
        externalSearchPartialProducer,
        setExternalSearchPartialProducer,

        // Table Data State
        externalFetchedWines,
        setExternalFetchedWines,
        externalFetchedProducers,
        setExternalFetchedProducers,
        externalSelectedWineRows,
        setExternalSelectedWineRows,
        externalMergeModalWines,
        setExternalMergeModalWines,

        // Pagination State
        externalWinesPage,
        setExternalWinesPage,
        externalProducersPage,
        setExternalProducersPage,
        externalHasMoreWines,
        setExternalHasMoreWines,
        externalHasMoreProducers,
        setExternalHasMoreProducers,
        externalTotalRowsOfWines,
        setExternalTotalRowsOfWines,
        externalTotalRowsOfProducers,
        setExternalTotalRowsOfProducers,

        // UI State
        externalShowMergeModal,
        setExternalShowMergeModal,
        externalShowProducerModal,
        setExternalShowProducerModal,
        externalSelectedProducerId,
        setExternalSelectedProducerId,

        // External Input File Data State
        externalNumberOfFiles,
        setExternalNumberOfFiles,
        externalUniqueKey,
        setExternalUniqueKey,
        externalFileName,
        setExternalFileName,
        externalTableData,
        setExternalTableData,
        externalCurrentIndex,
        setExternalCurrentIndex,
        externalDataLoaded,
        setExternalDataLoaded,
        externalMatchedWine,
        setExternalMatchedWine,
      }}
    >
      {children}
    </ExternalInputContext.Provider>
  );
};

export const useExternalInputContext = () => {
  const ctx = useContext(ExternalInputContext);
  if (!ctx) throw new Error('useExternalInputContext must be used within an ExternalInputProvider');
  return ctx;
};
